import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Activity, Download, Eye, HeartPulse, MapPinned, ScanEye, Smartphone, Stethoscope, Users } from 'lucide-react';
import { QrImage } from './QrImage';
import { formatDate, formatNumber } from '@/utils/format';

export interface PublicStats {
  hospitals: number;
  departments: number;
  practitioners: number;
  patients: number;
  newPatientsThisMonth: number;
  visitsToday: number;
  visitsThisYear: number;
  eyeExams: number;
  eyeSurgeries: number;
  provinces: number;
  villages: number;
  appAccounts: number;
  visitsByMonth: { month: string; visits: number }[];
  generatedAt: string;
}

export interface AndroidApp {
  id: 'patient' | 'staff';
  file: string;
  size: number;
  sha256: string;
}

/** downloads/android.json written by scripts/publish-android.sh */
export interface AndroidRelease {
  version: string;
  versionCode: number;
  builtAt: string;
  minAndroid?: string;
  apps: AndroidApp[];
}

/** JSON from a public URL; null when missing (the SPA fallback answers HTML). */
async function publicJson<T>(url: string): Promise<T | null> {
  const res = await fetch(url, {
    headers: { Accept: 'application/json' },
    cache: 'no-cache',
  });
  if (!res.ok || !(res.headers.get('content-type') ?? '').includes('json')) return null;
  return (await res.json()) as T;
}

export interface PublicBranding {
  hospitalId?: string;
  code?: string;
  name?: string;
  nameEn?: string | null;
  hasPhoto?: boolean;
  photoUpdatedAt?: string | null;
}

/** Name and logo for the sign-in page: the hospital of the sign-in link (?h=CODE), else the deployment's first hospital. */
export function usePublicBranding(code?: string | null) {
  return useQuery({
    queryKey: ['public-branding', code ?? ''],
    queryFn: () => publicJson<PublicBranding>(`/api/v1/public/branding${code ? `?h=${encodeURIComponent(code)}` : ''}`),
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

export function usePublicStats() {
  return useQuery({
    queryKey: ['public-stats'],
    queryFn: () => publicJson<PublicStats>('/api/v1/public/stats'),
    staleTime: 60_000,
    refetchInterval: 120_000,
    retry: 1,
  });
}

export function useAndroidRelease() {
  return useQuery({
    queryKey: ['android-release'],
    queryFn: () => publicJson<AndroidRelease>('/downloads/android.json'),
    staleTime: 300_000,
    retry: 0,
  });
}

const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** True once the element has been on screen (stats below the fold on phones start when seen). */
function useSeen<T extends HTMLElement>() {
  const [el, setEl] = useState<T | null>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (seen || !el) return;
    if (!('IntersectionObserver' in window)) return setSeen(true);
    const io = new IntersectionObserver((es) => es.some((e) => e.isIntersecting) && setSeen(true), { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, [el, seen]);
  return [setEl, seen] as const;
}

/**
 * A number that runs up to `target` (ease-out), like an odometer. Later changes (the stats
 * refresh every two minutes) run from the number shown to the new one.
 */
function useCountUp(target: number, run: boolean, delay = 0, duration = 1800) {
  const [value, setValue] = useState(() => (reducedMotion() ? target : 0));
  const shown = useRef(value);
  useEffect(() => {
    if (!run) return;
    if (reducedMotion() || shown.current === target) {
      shown.current = target;
      setValue(target);
      return;
    }
    const from = shown.current;
    let raf = 0;
    let t0: number | null = null;
    const step = (now: number) => {
      t0 ??= now;
      const p = Math.min(1, (now - t0) / duration);
      const eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
      const v = Math.round(from + (target - from) * eased);
      shown.current = v;
      setValue(v);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    const timer = window.setTimeout(() => (raf = requestAnimationFrame(step)), delay);
    // Background tabs pause animation frames: make sure the real number is shown anyway.
    const settle = window.setTimeout(() => {
      cancelAnimationFrame(raf);
      shown.current = target;
      setValue(target);
    }, delay + duration + 400);
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(settle);
      cancelAnimationFrame(raf);
    };
  }, [target, run, delay, duration]);
  return value;
}

interface Tile {
  icon: ReactNode;
  value: number;
  label: string;
  subKey: string;
  subCount?: number;
}

function StatTile({ tile, run, index }: { tile: Tile; run: boolean; index: number }) {
  const { t } = useTranslation();
  const delay = 250 + index * 120;
  const value = useCountUp(tile.value, run, delay);
  const sub = useCountUp(tile.subCount ?? 0, run && tile.subCount != null, delay + 300, 1400);
  const done = value === tile.value;
  return (
    <div className={`login-stat${done ? ' done' : ''}`}>
      <span className="ic">{tile.icon}</span>
      <div>
        <strong className="count" aria-label={formatNumber(tile.value)}>
          {formatNumber(value)}
        </strong>
        <span className="label">{tile.label}</span>
        <span className="sub">{tile.subCount != null ? t(tile.subKey, { count: sub }) : t(tile.subKey)}</span>
      </div>
    </div>
  );
}

function MonthBars({ data }: { data: PublicStats['visitsByMonth'] }) {
  const { t } = useTranslation();
  const max = Math.max(1, ...data.map((d) => d.visits));
  return (
    <div className="login-bars" role="img" aria-label={t('auth.stats.visitsByMonth')}>
      {data.map((d, i) => (
        <div
          key={d.month}
          className={`login-bar${i === data.length - 1 ? ' current' : ''}`}
          title={`${d.month}: ${formatNumber(d.visits)}`}
        >
          <span style={{ height: `${Math.max(4, (d.visits / max) * 100)}%`, animationDelay: `${600 + i * 70}ms` }} />
          <small>
            {t(`months.${Number(d.month.slice(5, 7))}`, {
              defaultValue: d.month.slice(5, 7),
            })}
          </small>
        </div>
      ))}
    </div>
  );
}

/** Live, aggregate figures of the system for the sign-in page. */
export function LoginStats({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const { data } = usePublicStats();
  const [ref, seen] = useSeen<HTMLDivElement>();
  if (!data) return null;
  const tiles: Tile[] = [
    { icon: <Users size={18} />, value: data.patients, label: t('auth.stats.patients'), subKey: 'auth.stats.newThisMonth', subCount: data.newPatientsThisMonth },
    { icon: <Activity size={18} />, value: data.visitsThisYear, label: t('auth.stats.visitsThisYear'), subKey: 'auth.stats.today', subCount: data.visitsToday },
    { icon: <Eye size={18} />, value: data.eyeExams, label: t('auth.stats.eyeExams'), subKey: 'auth.stats.surgeries', subCount: data.eyeSurgeries },
    { icon: <MapPinned size={18} />, value: data.villages, label: t('auth.stats.villages'), subKey: 'auth.stats.provinces', subCount: data.provinces },
    { icon: <ScanEye size={18} />, value: data.departments, label: t('auth.stats.departments'), subKey: 'auth.stats.practitioners', subCount: data.practitioners },
    { icon: <Smartphone size={18} />, value: data.appAccounts, label: t('auth.stats.appAccounts'), subKey: 'auth.stats.patientApp' },
  ];
  return (
    <div ref={ref} className={`login-stats${compact ? ' compact' : ''}${seen ? ' seen' : ''}`}>
      <div className="login-stats-head">
        <span className="live-dot" aria-hidden />
        {t('auth.stats.title')}
      </div>
      <div className="login-stat-grid">
        {(compact ? tiles.slice(0, 4) : tiles).map((s, i) => (
          <StatTile key={s.label} tile={s} run={seen} index={i} />
        ))}
      </div>
      {!compact && data.visitsByMonth?.length > 0 && (
        <div className="login-chart">
          <span className="label">{t('auth.stats.visitsByMonth')}</span>
          <MonthBars data={data.visitsByMonth} />
        </div>
      )}
    </div>
  );
}

function formatSize(bytes: number) {
  return `${formatNumber(bytes / 1024 / 1024, 1)} MB`;
}

const APP_ICON: Record<AndroidApp['id'], ReactNode> = {
  patient: <HeartPulse size={22} />,
  staff: <Stethoscope size={22} />,
};

/** Download cards for the two Android apps (shown only when published). */
export function AppDownloadCard() {
  const { t } = useTranslation();
  const { data } = useAndroidRelease();
  const apps = data?.apps ?? [];
  if (!data || apps.length === 0) return null;
  const order: AndroidApp['id'][] = ['patient', 'staff'];
  return (
    <div className="app-downloads">
      <div className="app-downloads-head">
        <Smartphone size={18} />
        <strong>{t('auth.app.title')}</strong>
        <span className="small muted">
          {t('auth.app.version', { version: data.version, date: formatDate(data.builtAt) })}
        </span>
      </div>
      {[...apps]
        .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
        .map((app) => {
          const href = `/downloads/${app.file}`;
          return (
            <div key={app.id} className={`app-download ${app.id}`}>
              <div className="app-download-main">
                <div className="app-download-icon" aria-hidden>
                  {APP_ICON[app.id]}
                </div>
                <div className="app-download-text">
                  <strong>{t(`auth.app.${app.id}.name`)}</strong>
                  <span className="small muted">{t(`auth.app.${app.id}.desc`)}</span>
                </div>
              </div>
              <a className="btn primary app-download-btn" href={href} download={app.file}>
                <Download size={18} />
                {t('auth.app.download')} · {formatSize(app.size)}
              </a>
              <div className="app-download-qr">
                <QrImage value={new URL(href, window.location.origin).toString()} size={84} label={t('auth.app.scan')} />
                <span className="small muted">{t('auth.app.scan')}</span>
              </div>
            </div>
          );
        })}
      <details className="small muted app-download-help">
        <summary>{t('auth.app.howTo')}</summary>
        <ol>
          <li>{t('auth.app.step1')}</li>
          <li>{t('auth.app.step2')}</li>
          <li>{t('auth.app.step3')}</li>
        </ol>
        {apps.map((app) => (
          <p key={app.id} className="mono" style={{ wordBreak: 'break-all' }}>
            {app.file} SHA-256: {app.sha256}
          </p>
        ))}
      </details>
    </div>
  );
}
